import { Component, Input, OnInit } from '@angular/core';
import { DataAnalysisApiService } from '../../services/api/data-analysis-api.service';
import { UtilService } from '../../../../core/services/util/util.service';
import { PlatformService } from '../../../../core/services/platform/platform.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Color, LegendPosition, ScaleType } from '@swimlane/ngx-charts';
import { faArchive, faCaretDown, faCaretUp, faSort, faXmark } from '@fortawesome/free-solid-svg-icons';

interface ChartDataset {
  name: string;
  series: Series[]
}
interface Series {
  name: string;
  value: number;
}

interface TableDataset {
  sku: string;
  name: string;
  stock: number;
  channels: ChannelSummary[];
}
interface ChannelSummary {
  channel: string;
  total_amount: number;
  total_qty: number;
}

@Component({
  selector: 'app-data-analysis-product',
  templateUrl: './data-analysis-product.component.html',
  styleUrl: './data-analysis-product.component.css'
})
export class DataAnalysisProductComponent implements OnInit {

  @Input() branchMode = false;

  faCaretDown = faCaretDown;
  faCaretUp = faCaretUp;
  faSort = faSort;
  faXmark = faXmark
  faArchive = faArchive
  legendPosition = LegendPosition.Right

  tableDatasets: TableDataset[] = []

  channels: any[] = [];
  channelFiltered: any[] = [];

  products: Array<{ sku: string; name: string, in_stock: number }> = [];
  keyword: string = '';
  productsFilter: Array<{ sku: string; name: string }> = [];
  showProductFilter = false;
  productSelected: Array<{ sku: string; name: string }> = [];

  productSummary: Array<{ channel: string; sku: string; total_qty: number; total_amount: number }> = []
  productFiltered: Array<{ sku: string; total_qty: number; total_amount: number }> = []

  countDataSource = 0;
  pageSize = 10;
  pageIndex = 0;
  limit = 10;
  offset = 0;

  displayData: string = 'total_qty';
  channelSelected: string[] = [];
  readonly ALL_CHANNEL_MARKER = '__all__';

  selectedDate: { type: string, value: string } = { type: 'daypass', value: 'month' };

  form: FormGroup;
  loading: boolean = false;

  selectedRow: string = '';
  period: number = 30;
  performanceRows: Array<{ sku: string; channel: string; date: string; total_amount: number; total_qty: number; }> = []
  performanceDatasets: ChartDataset[] = []
  chartLoaded: boolean = false;

  sortSelected: string = '';
  sortBy: string = 'max';

  breakdownLoading: boolean = false;
  breakdownItems: Array<{ shop_ref: string; platform_name: string; color?: string; sellable: number }> = [];

  private fallbackColors = new Map<string, string>();
  performanceChartScheme: Color = { name: 'channel-line', selectable: true, group: ScaleType.Ordinal, domain: [] };

  constructor(
    private dataAnalysisApiService: DataAnalysisApiService,
    private utilService: UtilService,
    private platformService: PlatformService,
    private fb: FormBuilder,
  ) {
    this.form = this.fb.group({
      date: this.computeDateRange(this.selectedDate),
      sku: [''],
      channel: ['']
    });

  }

  handleOptionSelected(selectedDate: { type: string, value: string }) {
    this.selectedDate = selectedDate;
    this.form.get('date')?.setValue(this.computeDateRange(selectedDate));
  }

  private computeDateRange(option: { type: string, value: string }): string {
    const dayjs = this.utilService.dayjs;
    if (option.type === 'date-range') {
      const [s, e] = option.value.split(',');
      return `${dayjs(s).format('YYYY-MM-DD')},${dayjs(e).format('YYYY-MM-DD')}`;
    }
    if (option.value === '0') {
      const d = dayjs().format('YYYY-MM-DD');
      return `${d},${d}`;
    }
    if (option.value === '1') {
      const d = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
      return `${d},${d}`;
    }
    if (option.value === 'month') {
      const start = dayjs().startOf('month').format('YYYY-MM-DD');
      const end = dayjs().format('YYYY-MM-DD');
      return `${start},${end}`;
    }
    const start = dayjs().subtract(parseInt(option.value), 'day').format('YYYY-MM-DD');
    const end = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    return `${start},${end}`;
  }

  ngOnInit(): void {
    const all = this.platformService?.channels || [];
    this.channels = all.filter(c => !!c.is_branch === this.branchMode);
    this.channelFiltered = this.channels;
    this.getDataReport();
  }

  async getDataReport() {
    if (this.loading) return;
    this.loading = true;
    const value = this.form.getRawValue()

    let filter: any = { limit: this.limit, offset: this.offset, sort_field: this.displayData, sort_by: this.sortBy }
    if (value.date) {
      filter['date'] = value.date
    }
    if (this.channelSelected.length > 0) {
      filter['field_channel'] = this.channelSelected.join()
      this.channelFiltered = this.channels.filter(c => this.channelSelected.includes(c.shop_ref))
    } else {
      filter['field_channel'] = this.channels.map((item) => item.shop_ref).toString()
      this.channelFiltered = this.channels
    }
    if (this.productSelected.length > 0) {
      filter['sku'] = this.productSelected.map(e => e.sku).join()
    }
    if (this.sortSelected) {
      filter['channel'] = this.sortSelected
    }


    this.productSummary = [];
    this.productFiltered = [];
    this.countDataSource = 0;
    this.selectedRow = ''

    try {
      const res: any = await this.dataAnalysisApiService.getProductSummaryReport(filter);
      if (res.success) {
        this.productFiltered = res.data.products;
        this.productSummary = res.data.summary;
        this.countDataSource = res.data.count;
        const uniqueSku = [...new Set(this.productFiltered.map(e => e.sku))];
        if (uniqueSku.length > 0) {
          await this.findAllProduct({ sku: uniqueSku.join(), filed: 'sku,name' });
        }
      }
      this.setFormattingData()
    } catch {
      this.productFiltered = [];
      this.productSummary = [];
      this.tableDatasets = [];
      this.countDataSource = 0;
    } finally {
      this.loading = false;
    }
  }

  async getAllSummaryReport(sku: string) {
    this.chartLoaded = false;
    let filter: any = {
      sku,
      date: `${this.utilService.dayjs().subtract(this.period, 'day').format('YYYY-MM-DD')},${this.utilService.dayjs().format('YYYY-MM-DD')}`
    }
    if (this.channelSelected.length > 0) {
      filter['channel'] = this.channelSelected.join()
    } else {
      filter['channel'] = this.channels.map((item) => item.shop_ref).toString()
    }
    try {
      const res: any = await this.dataAnalysisApiService.getProductDailyReport(filter);
      if (res.success) {
        this.performanceRows = res.data
        this.setFormattingPerformanceChart()
      } else {
        this.performanceRows = []
        this.performanceDatasets = []
      }
    } catch {
      this.performanceRows = []
      this.performanceDatasets = []
    } finally {
      this.chartLoaded = true;
    }
  }

  async searchProduct(keyword: string) {
    return await this.dataAnalysisApiService.searchProduct({ keyword, limit: 20 }).then((res: any) => {
      if (res.success) {
        return res.data;
      }
      return []
    })
  }

  async findAllProduct(filter: any) {
    await this.dataAnalysisApiService.getAllProduct(filter).then((res: any) => {
      if (res.success) {
        this.products = res.data;
      } else {
        this.products = []
      }
    })
    if (this.branchMode && this.products.length > 0) {
      await this.applyBranchStock();
    }
  }

  async loadStockBreakdown(sku: string) {
    this.breakdownLoading = true;
    this.breakdownItems = [];
    const channel = this.channelFiltered.map(c => c.shop_ref);
    if (!sku || channel.length === 0) {
      this.breakdownLoading = false;
      return;
    }
    try {
      const res: any = await this.dataAnalysisApiService.getStockDetail({ sku, channel });
      if (res?.success) {
        const stockMap = new Map<string, number>(
          (res.data || []).map((e: any) => [e.channel, e.sellable])
        );
        this.breakdownItems = this.channelFiltered.map(c => ({
          shop_ref: c.shop_ref,
          platform_name: c.platform_name,
          color: c.color,
          sellable: stockMap.get(c.shop_ref) ?? 0,
        })).sort((a, b) => b.sellable - a.sellable);
      }
    } catch {
      this.breakdownItems = [];
    } finally {
      this.breakdownLoading = false;
    }
  }

  private async applyBranchStock() {
    const sku = this.products.map(p => p.sku);
    const channel = this.channelFiltered.map(c => c.shop_ref);
    if (sku.length === 0 || channel.length === 0) return;
    try {
      const res: any = await this.dataAnalysisApiService.getStockBySku({ sku, channel });
      if (res?.success) {
        const stockMap = new Map<string, number>(
          (res.data || []).map((e: any) => [e.sku, e.sellable])
        );
        this.products = this.products.map(p => ({ ...p, in_stock: stockMap.get(p.sku) ?? 0 }));
      }
    } catch { }
  }

  readonly TOTAL_ROW_NAME = 'รวม';

  setFormattingData() {
    const productRows: TableDataset[] = this.productFiltered.map(product => {
      const channels: ChannelSummary[] = this.channelFiltered.map(channel => {
        const res = this.productSummary.find(e => e.channel === channel.shop_ref && e.sku === product.sku);
        return {
          channel: channel.shop_ref,
          total_amount: res?.total_amount || 0,
          total_qty: res?.total_qty || 0
        };
      });
      channels.push({
        channel: '',
        total_amount: channels.reduce((n, { total_amount }) => n + total_amount, 0),
        total_qty: channels.reduce((n, { total_qty }) => n + total_qty, 0)
      });
      return {
        sku: product.sku,
        stock: this.products.find(p => p.sku === product.sku)?.in_stock || 0,
        name: this.products.find(p => p.sku === product.sku)?.name || '',
        channels
      };
    });

    this.tableDatasets = productRows.length > 0
      ? [...productRows, this.buildTotalRow(productRows)]
      : [];
  }

  private rebuildPerformanceChartScheme(): void {
    this.performanceChartScheme = {
      name: 'channel-line',
      selectable: true,
      group: ScaleType.Ordinal,
      domain: this.channels.map(c => c.color || this.channelFallbackColor(c.shop_ref))
    };
  }

  private channelFallbackColor(key: string): string {
    if (!this.fallbackColors.has(key)) {
      let hash = 0;
      for (let i = 0; i < key.length; i++) {
        hash = ((hash << 5) - hash) + key.charCodeAt(i);
        hash |= 0;
      }
      const hue = Math.abs(hash) % 360;
      this.fallbackColors.set(key, `hsl(${hue}, 65%, 55%)`);
    }
    return this.fallbackColors.get(key)!;
  }

  private buildTotalRow(rows: TableDataset[]): TableDataset {
    const channelCount = rows[0]?.channels.length || 0;
    const channels: ChannelSummary[] = Array.from({ length: channelCount }, (_, i) => ({
      channel: rows[0].channels[i].channel,
      total_amount: rows.reduce((s, r) => s + (r.channels[i]?.total_amount || 0), 0),
      total_qty: rows.reduce((s, r) => s + (r.channels[i]?.total_qty || 0), 0)
    }));
    return {
      sku: '',
      name: this.TOTAL_ROW_NAME,
      stock: rows.reduce((s, r) => s + r.stock, 0),
      channels
    };
  }

  setFormattingPerformanceChart() {
    this.rebuildPerformanceChartScheme();
    this.performanceDatasets = []
    for (const channel of this.channels) {

      const series = [];
      const performanceObj: any = this.performanceRows.filter((e: any) => e.channel === channel.shop_ref).reduce((a, v) => ({ ...a, [v.date]: v }), {})
      for (let index = this.period; index >= 0; index--) {
        const date = this.utilService.dayjs().subtract(index, 'day').format('YYYY-MM-DD');
        series.push({ name: date, value: this.displayData === 'total_qty' ? (performanceObj[date]?.total_qty || 0) : (performanceObj[date]?.total_amount || 0) })
      }

      this.performanceDatasets.push({
        name: channel.platform_name,
        series: series
      })

    }
  }

  get displayedChannelSelection(): string[] {
    if (this.channels.length > 0 && this.channelSelected.length === this.channels.length) {
      return [this.ALL_CHANNEL_MARKER, ...this.channelSelected];
    }
    return this.channelSelected;
  }

  onChannelSelectionChange(values: any[]) {
    const list = Array.isArray(values) ? values : [];
    const wasAllSelected = this.channels.length > 0
      && this.channelSelected.length === this.channels.length;
    const newHasMarker = list.includes(this.ALL_CHANNEL_MARKER);

    if (newHasMarker !== wasAllSelected) {
      this.channelSelected = newHasMarker
        ? this.channels.map(c => c.shop_ref)
        : [];
    } else {
      this.channelSelected = list.filter((v: unknown): v is string =>
        typeof v === 'string' && v !== this.ALL_CHANNEL_MARKER
      );
    }
  }

  removeChannelSelected(channel: any) {
    this.channelSelected = this.channelSelected.filter((e: string) => e !== channel.shop_ref)
  }

  removeChannelSelectedAll() {
    this.channelSelected = []
  }

  displaychannel() {
    return this.channels.filter(c => this.channelSelected.includes(c.shop_ref))
  }

  displayDataOnchange(event: any) {
    this.displayData = event.value
    this.offset = 0;
    this.pageIndex = 0;
    this.getDataReport();
  }

async productSearch(dataSearch: any) {
    if (this.keyword.length > 2) {
      try {
        const uniquesku = this.productSelected.map(e => e.sku);
        const results = await this.searchProduct(this.keyword);
        this.productsFilter = (results || []).filter((e: any) => !uniquesku.includes(e.sku))
        this.showProductFilter = true;
      } catch {
        this.productsFilter = [];
        this.showProductFilter = false;
      }
    } else {
      this.showProductFilter = false;
    }
  }

  selectProduct(product: any) {
    this.productSelected.push(product)
    this.productsFilter = this.productsFilter.filter((e: any) => e.sku !== product.sku)
    if (this.productsFilter.length === 0) {
      this.showProductFilter = false;
    }
  }

  removeProductSelected(product: any) {
    this.productSelected = this.productSelected.filter(e => e.sku !== product.sku)
  }

  removeProductSelectedAll() {
    this.productSelected = []
  }

  callBackPageChange = (data: any): void => {
    if (this.offset !== data.offset || this.limit !== data.limit) {
      this.offset = data.offset;
      this.limit = data.limit;
      this.getDataReport()
    }

    if (data.pageIndex !== undefined) {
      this.pageIndex = data.pageIndex;
    }
    if (data.limit !== undefined) {
      this.pageSize = data.limit;
    }
  }

  submitForm() {
    this.offset = 0;
    this.pageIndex = 0;
    this.sortSelected = '';
    this.sortBy = 'max'
    this.getDataReport();
  }

  selectedRowOnChange(row: any) {
    if (this.selectedRow === row.sku) {
      this.selectedRow = ''
    } else {
      this.selectedRow = row.sku
      this.getAllSummaryReport(this.selectedRow)
    }
  }

  sortData(shop_ref: string) {

    if (this.sortSelected !== shop_ref) {
      this.sortSelected = shop_ref;
      this.sortBy = 'max'
    } else {
      if (!this.sortBy) this.sortBy = 'max'
      else if (this.sortBy === 'max') this.sortBy = 'min'
      else if (this.sortBy === 'min') this.sortBy = 'max'
    }

    this.offset = 0;
    this.pageIndex = 0;
    this.getDataReport();
  }

  clearSearch() {
    this.keyword = ''
    this.showProductFilter = false
  }

}
